import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Stack,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import RscLogo from "../../assests/logo/rscLogo";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { enable2FA, verify2FASetup } from "../../reducers/authentication.slice";
import CustomLoader from "../../components/CustomLoader";
import { AuthContext } from "../../context/authContext";

const Setup2FAPageWrapper = styled(Stack)(({ theme }) => ({
  minHeight: "100vh",
  height: "100%",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: "#000000",
  overflow: "none",
}));

const Setup2FAPageComponent = styled(Stack)(({ theme }) => ({
  padding: "32px",
  backgroundColor: "#101010",
  borderRadius: "10px",
  border: "1px solid #ffffff85",
  maxWidth: "900px",
  width: "90%",
  [theme.breakpoints.down("md")]: {
    maxWidth: "600px",
    padding: "24px",
  },
  [theme.breakpoints.down("sm")]: {
    maxWidth: "400px",
    padding: "20px",
  },
}));

const LogoSectionWrapper = styled(Stack)(({ theme }) => ({
  justifyContent: "space-between",
  alignItems: "center",
  flexDirection: "row",
}));

const LogoDisplaySection = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(1.5),
  backgroundColor: "white",
  width: "fit-content",
  borderRadius: "3px",
  marginBottom: "20px",
}));

const QRCodeContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "#ffffff",
  padding: "16px",
  borderRadius: "8px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "fit-content",
}));

const QRSection = styled(Stack)(({ theme }) => ({
  flex: 1,
  alignItems: "center",
  justifyContent: "center",
  padding: "16px",
  [theme.breakpoints.down("md")]: {
    padding: "0",
  },
}));

const VerificationSection = styled(Stack)(({ theme }) => ({
  flex: 1,
  justifyContent: "center",
  padding: "16px",
  [theme.breakpoints.down("md")]: {
    padding: "0",
    marginTop: "24px",
  },
}));

const ContentWrapper = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  gap: "32px",
  marginTop: "24px",
  alignItems: "center",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    gap: "0",
  },
}));

const Divider = styled(Box)(({ theme }) => ({
  width: "1px",
  backgroundColor: "#ffffff85",
  alignSelf: "stretch",
  [theme.breakpoints.down("md")]: {
    width: "60%",
    height: "1px",
    margin: "24px auto",
  },
}));

const SetupKeyBox = styled(Box)(({ theme }) => ({
  backgroundColor: "#1a1a1a",
  padding: "12px",
  borderRadius: "8px",
  border: "1px solid #ffffff85",
  marginBottom: "20px",
  wordBreak: "break-all",
}));

const Setup2FAPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { showToast } = React.useContext(AuthContext);

  const [showLoader, setShowLoader] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [setupKey, setSetupKey] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    // Get user data from location state
    if (location.state?.userData) {
      const userData = location.state.userData;
      setUserData(userData);
      // Call enable2FA API to get QR code with user_id
      setShowLoader(true);
      const payload = {
        user_id: userData.id, // Pass user_id from logged in user data
        two_fa_challenge: userData.two_fa_challenge, // One-time code from the login response
      };
      dispatch(enable2FA(payload)).then((res) => {
        setShowLoader(false);
        if (res?.error && Object.keys(res?.error)?.length > 0) {
          showToast(res?.error?.message || "Failed to setup 2FA", "error");
          navigate("/");
          return;
        }
        if (res?.payload) {
          // Backend returns base64 string, need to format as data URL for img tag
          setQrCodeUrl(`data:image/png;base64,${res.payload.qr_code}`);
          setSetupKey(res.payload.setup_key);
        }
      });
    } else {
      // If no user data, redirect to login
      navigate("/");
    }
  }, [location.state, dispatch, navigate, showToast]);

  const handleVerifySetup = (e) => {
    e.preventDefault();
    if (!verificationCode || verificationCode.length !== 6) {
      showToast("Please enter a valid 6-digit code", "error");
      return;
    }

    setShowLoader(true);
    const payload = {
      user_id: userData.id, // Pass user_id
      totp_code: verificationCode, // Match backend field name
      trusted_device: userData.trusted_device || false, // Pass trusted_device flag from login
      two_fa_challenge: userData.two_fa_challenge, // One-time code from the login response
    };

    dispatch(verify2FASetup(payload)).then((res) => {
      setShowLoader(false);
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        showToast(res?.error?.message || "Verification failed", "error");
        return;
      }

      if (res?.payload) {
        showToast(
          "2FA setup successful! Redirecting to dashboard...",
          "success"
        );

        // Backend now returns full login response with 48-hour token
        // Save the full user data with final token
        localStorage.setItem("user", JSON.stringify(res.payload));

        // Redirect to appropriate dashboard based on role
        const userRole = res.payload.role;
        if (userRole === "MEMBER") {
          navigate("/user-dashboard");
        } else if (userRole === "MANAGER") {
          navigate("/manager-dashboard");
        } else if (userRole === "SUPERADMIN") {
          navigate("/admin-dashboard");
        } else {
          showToast("Invalid User", "error");
          navigate("/login");
        }
      }
    });
  };

  const handleBack = () => {
    // Clear temp token when user cancels 2FA setup
    localStorage.removeItem("user");
    sessionStorage.removeItem("user");

    // Redirect to login page
    navigate("/login");
  };

  return (
    <>
      <CustomLoader open={showLoader} />
      <Setup2FAPageWrapper>
        <Setup2FAPageComponent>
          <LogoSectionWrapper>
            <LogoDisplaySection>
              <RscLogo color="black" />
            </LogoDisplaySection>
            <Stack
              gap={1}
              flexDirection={"row"}
              alignContent={"center"}
              color={"#ffffff85"}
              sx={{ cursor: "pointer" }}
              onClick={handleBack}
            >
              <KeyboardBackspaceIcon />{" "}
              <Typography fontFamily={"inter"}>Back</Typography>
            </Stack>
          </LogoSectionWrapper>

          <Typography
            sx={{
              fontSize: "1.7rem",
              color: "white",
              fontWeight: 700,
              marginBottom: "8px",
            }}
          >
            Set Up Two-Factor Authentication
          </Typography>

          <Typography
            sx={{
              fontSize: "0.9rem",
              color: "#ffffff85",
              marginBottom: "8px",
            }}
          >
            Secure your account with an additional layer of protection
          </Typography>

          <ContentWrapper>
            {/* Left Section - QR Code and Setup Key */}
            <QRSection>
              <Typography
                sx={{
                  fontSize: "1rem",
                  color: "white",
                  fontWeight: 600,
                  marginBottom: "8px",
                  textAlign: "center",
                }}
              >
                Scan QR Code
              </Typography>

              <Typography
                sx={{
                  fontSize: "0.85rem",
                  color: "#ffffff85",
                  marginBottom: "12px",
                  textAlign: "center",
                }}
              >
                Use Google Authenticator app to scan
              </Typography>

              {qrCodeUrl && (
                <QRCodeContainer>
                  <img
                    src={qrCodeUrl}
                    alt="2FA QR Code"
                    style={{ width: "180px", height: "180px" }}
                  />
                </QRCodeContainer>
              )}

              <Typography
                sx={{
                  fontSize: "0.85rem",
                  color: "#ffffff85",
                  marginTop: "16px",
                  marginBottom: "8px",
                  textAlign: "center",
                }}
              >
                Or enter this setup key manually:
              </Typography>

              {setupKey && (
                <SetupKeyBox>
                  <Typography
                    sx={{
                      fontSize: "0.85rem",
                      color: "white",
                      fontFamily: "monospace",
                      textAlign: "center",
                    }}
                  >
                    {setupKey}
                  </Typography>
                </SetupKeyBox>
              )}
            </QRSection>

            {/* Divider */}
            <Divider />

            {/* Right Section - Verification Form */}
            <VerificationSection>
              <Typography
                sx={{
                  fontSize: "1rem",
                  color: "white",
                  fontWeight: 600,
                  marginBottom: "8px",
                }}
              >
                Enter Verification Code
              </Typography>

              <Typography
                sx={{
                  fontSize: "0.85rem",
                  color: "#ffffff85",
                  marginBottom: "16px",
                }}
              >
                Enter the 6-digit code from your authenticator app
              </Typography>

              <form onSubmit={handleVerifySetup}>
                <TextField
                  fullWidth
                  value={verificationCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                    setVerificationCode(value);
                  }}
                  placeholder="000000"
                  inputProps={{
                    maxLength: 6,
                    style: {
                      textAlign: "center",
                      fontSize: "1.5rem",
                      letterSpacing: "0.5rem",
                      fontFamily: "monospace",
                    },
                  }}
                  sx={{
                    backgroundColor: "#1a1a1a",
                    borderRadius: "8px",
                    input: { color: "white" },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#ffffff85",
                      },
                      "&:hover fieldset": {
                        borderColor: "#ffffff",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#ffffff",
                      },
                    },
                  }}
                />

                <Button type="submit" variant="contained" fullWidth>
                  Verify and Enable 2FA
                </Button>
              </form>
            </VerificationSection>
          </ContentWrapper>
        </Setup2FAPageComponent>
      </Setup2FAPageWrapper>
    </>
  );
};

export default Setup2FAPage;
